import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MatchingService, type GuideDetails, type GuideEnhancedProfile } from '../../services/matchingService';
import { GuideProfileView } from '../profiles/GuideProfileView';
import { ConsultationScheduler } from '../booking/ConsultationScheduler';
import { AccountPasswordSetup } from '../onboarding/AccountPasswordSetup';
import { EmailConfirmation } from '../onboarding/EmailConfirmation';
import { BookingSuccess } from '../booking/BookingSuccess';
import type { ScheduledSession } from '../../services/schedulingService';
import { getSupabaseClient } from '../../lib/supabase';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';

/**
 * Check if seeker has an existing scheduled session with the given guide
 */
async function checkExistingSessionWithGuide(guideId: string): Promise<boolean> {
  try {
    const supabase = getSupabaseClient();

    // Get current user
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return false;
    }

    // Check for any scheduled/confirmed sessions with this guide
    const { data: sessions, error: sessionsError } = await supabase
      .from('scheduled_sessions')
      .select('id')
      .eq('seeker_id', user.id)
      .eq('guide_id', guideId)
      .in('session_status', ['scheduled', 'confirmed', 'hold'])
      .limit(1);

    if (sessionsError) {
      console.error('Error checking existing sessions:', sessionsError);
      return false;
    }

    return sessions && sessions.length > 0;
  } catch (error) {
    console.error('Error in checkExistingSessionWithGuide:', error);
    return false;
  }
}

/**
 * Fetch the active match for an authenticated seeker from the database
 */
async function fetchActiveMatch(): Promise<{ guide: GuideDetails; matchScore: number } | null> {
  try {
    const supabase = getSupabaseClient();

    // Get current user - this is the auth user ID
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      console.log('No authenticated user to fetch match for');
      return null;
    }

    const userId = user.id;
    console.log('Fetching seeker_id for auth user:', userId);

    // Look up the seeker_id from the seekers table using the user_id
    const { data: seekerData, error: seekerError } = await supabase
      .from('seekers')
      .select('seeker_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (seekerError) {
      console.error('Error fetching seeker:', seekerError);
      return null;
    }

    if (!seekerData?.seeker_id) {
      console.log('No seeker_id found for user:', userId);
      return null;
    }

    const seekerId = seekerData.seeker_id;
    console.log('Found seeker_id:', seekerId);

    // Fetch the latest active match for this seeker using the seeker_id
    const { data: matchData, error: matchError } = await supabase
      .from('matches')
      .select('guide_id, match_score, score_breakdown, reasoning, created_at')
      .eq('seeker_id', seekerId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (matchError) {
      console.error('Error fetching match:', matchError);
      return null;
    }

    if (!matchData) {
      console.log('No match found for seeker:', seekerId);
      return null;
    }

    console.log('Found match:', matchData);

    // Fetch guide details
    const guideDetails = await MatchingService.getGuideDetails(matchData.guide_id);

    return {
      guide: guideDetails,
      matchScore: matchData.match_score
    };
  } catch (error) {
    console.error('Error in fetchActiveMatch:', error);
    return null;
  }
}

const ACTION_BUTTON_BASE_CLASSES =
  'inline-flex w-full items-center justify-center rounded-full border border-[#304031] bg-[#FFFCEF] px-6 py-4 text-lg font-semibold text-[#304031] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#304031] hover:text-[#FFFCEF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031] disabled:pointer-events-none disabled:translate-y-0 disabled:opacity-50';

/**
 * GuideCard - Individual guide card component for displaying a single guide match
 */
type GuideCardProps = {
  guide: GuideDetails;
  matchScore?: number;
  seekerVisibleMessages?: string[];
  enhancedProfile?: GuideEnhancedProfile | null;
  onScheduleConsultation: (guide: GuideDetails) => void;
  onViewProfile: (guide: GuideDetails) => void;
};

const GuideCard: React.FC<GuideCardProps> = ({
  guide,
  matchScore,
  seekerVisibleMessages,
  enhancedProfile,
  onScheduleConsultation,
  onViewProfile,
}) => {
  const [whyExpanded, setWhyExpanded] = useState(false);

  const guideName = guide.name;
  const location = guide.location;
  const specialty = MatchingService.formatGuideSpecialty(guide);
  const formattedLocation = location ? MatchingService.formatLocation(location) : null;

  // Use enhanced profile data if available
  const profileImage = enhancedProfile?.image_url;

  const experienceTypes =
    guide.experience_types
      ?.map(type => type?.trim())
      .filter((type): type is string => Boolean(type && type.length > 0)) ?? [];
  const supportTypes =
    guide.support_types
      ?.map(type => type?.trim())
      .filter((type): type is string => Boolean(type && type.length > 0)) ?? [];
  const focusAreas =
    guide.focus_areas
      ?.map(area => area?.trim())
      .filter((area): area is string => Boolean(area && area.length > 0)) ?? [];
  const specialGroups =
    guide.special_groups
      ?.map(group => group?.trim())
      .filter((group): group is string => Boolean(group && group.length > 0)) ?? [];

  const formatList = (items: string[], limit = 3) => items.slice(0, limit).join(', ');

  // Build match highlights
  const matchHighlights: string[] = [];
  if (specialty) {
    matchHighlights.push(`Primary specialty: ${specialty}.`);
  }
  if (experienceTypes.length) {
    matchHighlights.push(`Experienced with ${formatList(experienceTypes)} journeys.`);
  }
  if (supportTypes.length) {
    matchHighlights.push(`Supports seekers navigating ${formatList(supportTypes)}.`);
  }
  if (focusAreas.length) {
    matchHighlights.push(`Focus areas include ${formatList(focusAreas)}.`);
  }
  if (specialGroups.length) {
    matchHighlights.push(`Comfortable guiding ${formatList(specialGroups)} communities.`);
  }
  if (formattedLocation) {
    matchHighlights.push(`Available near ${formattedLocation}${location ? '' : ' (virtual)'}.`);
  }
  if (!matchHighlights.length) {
    matchHighlights.push('Curated to align with your intentions and availability.');
  }

  // Helper for pills
  const shortName = (s: string) => s.split(/\s[—–-]\s/)[0].trim();
  const isValid = (s: string) => s.length > 0 && s.toLowerCase() !== 'none';

  const pills: string[] = [];
  if (specialty) {
    specialty.split(',').map(s => s.trim()).filter(isValid).forEach(s => pills.push(s));
  }
  experienceTypes.slice(0, 3).map(shortName).filter(isValid).forEach(t => pills.push(t));
  supportTypes.slice(0, 2).map(shortName).filter(isValid).forEach(t => pills.push(t));
  focusAreas.slice(0, 2).map(shortName).filter(isValid).forEach(t => pills.push(t));
  const uniquePills = [...new Set(pills)];

  const firstName = guideName?.split(' ')[0];
  const whyMessages = seekerVisibleMessages && seekerVisibleMessages.length > 0
    ? seekerVisibleMessages
    : matchHighlights;

  return (
    <div className="border-2 border-onboarding-card-green rounded-lg p-6 flex flex-col gap-4 w-full">
      {/* Top section: Avatar + Info */}
      <div className="flex flex-col lg:flex-row lg:gap-4 lg:items-start">
        {/* Avatar */}
        <div className="shrink-0">
          {profileImage ? (
            <img
              src={profileImage}
              alt={`Portrait of ${guideName}`}
              className="h-[104px] w-[104px] rounded-full object-cover"
              onError={event => {
                (event.target as HTMLImageElement).src = '/assets/guide-avatar.webp';
              }}
            />
          ) : (
            <div className="flex h-[104px] w-[104px] items-center justify-center rounded-full bg-onboarding-bg text-4xl text-onboarding-text-black">
              {guideName?.charAt(0) ?? 'G'}
            </div>
          )}
        </div>

        {/* Info column */}
        <div className="flex flex-col gap-2 w-full mt-4 lg:mt-0">
          <h2 className="font-recoleta text-2xl leading-[44px] tracking-tight text-onboarding-text-black ph-mask">
            {guideName}
          </h2>
          {/* Specialty pills */}
          {uniquePills.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {uniquePills.map((pill, i) => (
                <span
                  key={i}
                  className="inline-block rounded-full bg-onboarding-text-light px-1.5 py-1 text-xs leading-relaxed text-onboarding-text-black"
                >
                  {pill}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Collapsible "Why" section */}
      <div className="border-y border-onboarding-text-black/10 py-4">
        <button
          type="button"
          onClick={() => setWhyExpanded(!whyExpanded)}
          className="flex w-full items-center justify-between text-left"
        >
          <span className="font-helvetica text-base leading-relaxed tracking-tight text-onboarding-text-black">
            Why {firstName} matches your needs
          </span>
          <svg
            className={`h-6 w-6 text-onboarding-text-black transition-transform ${whyExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {whyExpanded && (
          <div className="flex flex-col gap-3 pt-4">
            {whyMessages.map((message, index) => {
              const lines = message.split('\n').filter(line => line.trim());
              const title = lines[0];
              const body = lines.slice(1).join(' ');

              return (
                <div key={index} className="flex gap-2.5">
                  <div className="mt-0.5 h-5 w-5 shrink-0">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" className="fill-onboarding-card-green" />
                      <path d="M8 12L11 15L16 9" className="stroke-onboarding-content-bg" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <p className="font-helvetica text-sm leading-normal text-onboarding-text-black">
                    {title}
                    {body && <><br />{body}</>}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA section */}
      <div className="flex flex-col gap-4">
        <p className="font-helvetica text-base leading-relaxed tracking-tight text-onboarding-text-black">
          Ready to schedule a free, private and no-pressure 20 minute consultation call?
        </p>
        <button
          type="button"
          onClick={() => onScheduleConsultation(guide)}
          className="w-full h-[54px] rounded-full bg-onboarding-card-green px-5 py-4 font-helvetica text-base leading-relaxed tracking-tight text-onboarding-content-bg transition hover:bg-onboarding-card-green/90"
        >
          Schedule Consultation
        </button>
        <button
          type="button"
          onClick={() => onViewProfile(guide)}
          className="w-full font-helvetica text-base leading-relaxed tracking-tight text-onboarding-primary text-center hover:underline"
        >
          View full profile
        </button>
      </div>
    </div>
  );
};

type GuideMatchProps = {
  variant: 'match' | 'no_match';
  guide?: GuideDetails;
  guides?: GuideDetails[];  // NEW: Support array of guides
  matchScore?: number;
  matchScores?: Record<string, number>;  // NEW: Scores by guide_id
  seekerVisibleMessages?: string[];
  seekerVisibleMessagesByGuide?: Record<string, string[]>;  // NEW: Messages by guide_id
  onBack?: () => void;
  onViewProfile?: (guide: GuideDetails) => void;
  onScheduleConsultation?: (guide: GuideDetails) => void;
};

export const GuideMatch: React.FC<GuideMatchProps> = ({
  variant,
  guide: propGuide,
  guides: propGuides,
  matchScore: propMatchScore,
  matchScores: propMatchScores,
  seekerVisibleMessages: propSeekerVisibleMessages,
  seekerVisibleMessagesByGuide: propSeekerVisibleMessagesByGuide,
}) => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  // State for multiple enhanced profiles keyed by guide_id
  const [enhancedProfiles, setEnhancedProfiles] = useState<Record<string, GuideEnhancedProfile | null>>({});
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [showProfileView, setShowProfileView] = useState(false);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState<string>('');
  const [showScheduler, setShowScheduler] = useState(false);
  const [showBookingSuccess, setShowBookingSuccess] = useState(false);
  const [completedSession, setCompletedSession] = useState<ScheduledSession | null>(null);
  const [selectedPassword, setSelectedPassword] = useState<string>('');
  const [accountSetupError, setAccountSetupError] = useState<string>('');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [fetchedGuides, setFetchedGuides] = useState<GuideDetails[]>([]);
  const [fetchedMatchScores, setFetchedMatchScores] = useState<Record<string, number>>({});
  const [isLoadingMatch, setIsLoadingMatch] = useState<boolean>(false);
  const [hasExistingSession, setHasExistingSession] = useState<boolean | null>(null);
  // Track which guide is currently selected for scheduler/profile view
  const [selectedGuide, setSelectedGuide] = useState<GuideDetails | null>(null);

  // DEVELOPMENT FLAG: Force mock data with 2 guides as shown in Figma
  // Set to false when backend supports multiple guides
  const USE_MOCK_DATA = false;

  // Build guides array
  let guides: GuideDetails[] = [];
  let matchScores: Record<string, number> = {};
  let seekerVisibleMessagesByGuide: Record<string, string[]> = {};

  if (USE_MOCK_DATA) {
    // Mock data matching Figma design
    const mockGuide1: GuideDetails = {
      guide_id: 'mock-guide-1',
      name: 'Guide 1',
      location: 'San Francisco, CA',
      bio: 'Experienced guide specializing in personal development and empowerment.',
      experience_types: ['1:1 Journeys or Sessions', 'Microdose Support'],
      support_types: ['Personal Development', 'Empowerment'],
      focus_areas: ['Personal Development', 'Empowerment'],
      special_groups: [],
      religious_beliefs: [],
      backgroundChecked: true,
    };

    const mockGuide2: GuideDetails = {
      guide_id: 'mock-guide-2',
      name: 'Guide 2',
      location: 'Los Angeles, CA',
      bio: 'Dedicated guide with focus on personal growth and transformation.',
      experience_types: ['1:1 Journeys or Sessions', 'Microdose Support'],
      support_types: ['Personal Development', 'Empowerment'],
      focus_areas: ['Personal Development', 'Empowerment'],
      special_groups: [],
      religious_beliefs: [],
      backgroundChecked: true,
    };

    guides = [mockGuide1, mockGuide2];
    matchScores = {
      'mock-guide-1': 95,
      'mock-guide-2': 92,
    };
    seekerVisibleMessagesByGuide = {
      'mock-guide-1': ['Strong alignment with your personal development goals', 'Extensive experience with 1:1 sessions'],
      'mock-guide-2': ['Great match for empowerment focus', 'Specializes in microdose support'],
    };
  } else {
    // Load from props
    guides = propGuides || [];
    matchScores = propMatchScores || {};
    seekerVisibleMessagesByGuide = propSeekerVisibleMessagesByGuide || {};

    // Support legacy single guide prop
    if (!guides.length && propGuide) {
      guides = [propGuide];
      if (propMatchScore && propGuide.guide_id) {
        matchScores[propGuide.guide_id] = propMatchScore;
      }
      if (propSeekerVisibleMessages && propGuide.guide_id) {
        seekerVisibleMessagesByGuide[propGuide.guide_id] = propSeekerVisibleMessages;
      }
    }

    // Merge fetched guides if no props provided
    if (!guides.length && fetchedGuides.length) {
      guides = fetchedGuides;
      matchScores = { ...matchScores, ...fetchedMatchScores };
    }

    // Try to load from localStorage - support both old (single) and new (array) formats
    try {
      const storedResult = localStorage.getItem('matching_result');
      if (storedResult && !guides.length) {
        const parsed = JSON.parse(storedResult);
        if (parsed.found) {
          // New format: array of guides
          if (parsed.guides && Array.isArray(parsed.guides)) {
            guides = parsed.guides;
            matchScores = parsed.matchScores || {};
            seekerVisibleMessagesByGuide = parsed.seekerVisibleMessagesByGuide || {};
          }
          // Old format: single guide - wrap in array
          else if (parsed.guide) {
            guides = [parsed.guide];
            if (parsed.matchScore && parsed.guide.guide_id) {
              matchScores[parsed.guide.guide_id] = parsed.matchScore;
            }
            if (parsed.seekerVisibleMessages && parsed.guide.guide_id) {
              seekerVisibleMessagesByGuide[parsed.guide.guide_id] = parsed.seekerVisibleMessages;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error reading matching result from localStorage:', error);
    }
  }

  // For backwards compatibility - get the first guide
  const guide = guides[0];
  const matchScore = guide?.guide_id ? matchScores[guide.guide_id] : undefined;

  // Fetch enhanced profiles for all guides
  useEffect(() => {
    if (!guides.length) return;
    if (isLoadingProfiles) return;

    // Find guides that need profile fetching
    const guideIdsToFetch = guides
      .filter(g => g.guide_id && enhancedProfiles[g.guide_id] === undefined)
      .map(g => g.guide_id);

    if (!guideIdsToFetch.length) return;

    let cancelled = false;

    setIsLoadingProfiles(true);

    // Fetch all profiles in parallel
    Promise.all(
      guideIdsToFetch.map(id =>
        MatchingService.getGuideEnhancedProfile(id)
          .then(profile => ({ id, profile }))
          .catch(error => {
            console.error(`Error fetching enhanced profile for ${id}:`, error);
            return { id, profile: null };
          })
      )
    )
      .then(results => {
        if (cancelled) return;
        const newProfiles: Record<string, GuideEnhancedProfile | null> = {};
        results.forEach(({ id, profile }) => {
          newProfiles[id] = profile;
        });
        setEnhancedProfiles(prev => ({ ...prev, ...newProfiles }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingProfiles(false);
      });

    return () => {
      cancelled = true;
    };
  }, [guides.map(g => g.guide_id).join(',')]);

  // Background check status is now handled within the GuideCard component if needed

  useEffect(() => {
    const supabase = getSupabaseClient();
    let cancelled = false;

    const checkAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error || !data.session?.user) {
          setIsAuthenticated(false);
          return;
        }

        const authUserId = data.session.user.id;

        // If there's a ghost_seeker_id, this is a fresh onboarding flow.
        // Verify the auth session actually belongs to THIS onboarding's seeker.
        const ghostSeekerId = localStorage.getItem('ghost_seeker_id');
        if (ghostSeekerId) {
          const { data: seekerRow } = await supabase
            .from('seekers')
            .select('user_id')
            .eq('seeker_id', ghostSeekerId)
            .maybeSingle();

          if (cancelled) return;

          if (!seekerRow?.user_id || seekerRow.user_id !== authUserId) {
            // Auth session doesn't match the onboarding seeker — treat as unauthenticated
            console.log('Auth session does not match onboarding seeker, requiring account setup');
            setIsAuthenticated(false);
            return;
          }
        }

        setIsAuthenticated(true);
      } catch (err) {
        if (!cancelled) {
          console.warn('Unexpected auth session error in match view', err);
          setIsAuthenticated(false);
        }
      }
    };

    checkAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch active match from database if authenticated and no guide data available.
  // Also runs when localStorage has cached data so we can detect if the matched
  // guide changed and invalidate the stale cache accordingly.
  useEffect(() => {
    // Skip if we already have guide data from props or in-memory state
    if (propGuide || propGuides?.length || fetchedGuides.length) {
      return;
    }

    // Only fetch if authenticated
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;

    // For authenticated seekers landing on this page without going through
    // the FindingMatch flow first, re-run matching via the edge function so
    // we get the full multi-guide response (matches the design's 2 guides).
    //
    // The previous implementation queried the `matches` table directly with
    // .limit(1) which always returned just the persisted "best match" — that
    // bypassed the multi-guide flow entirely and caused only 1 card to render.
    // We still fall back to the single-row DB query if findMatches fails for
    // any reason (preserves the previous behavior as a safety net).
    const loadActiveMatch = async () => {
      setIsLoadingMatch(true);

      try {
        // Resolve the seeker_id (matches the same logic FindingMatch uses)
        const supabase = getSupabaseClient();
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          if (!cancelled) setIsLoadingMatch(false);
          return;
        }
        const { data: seekerRow } = await supabase
          .from('seekers')
          .select('seeker_id')
          .eq('user_id', user.id)
          .maybeSingle();
        const seekerId = (seekerRow as any)?.seeker_id;
        if (!seekerId) {
          if (!cancelled) setIsLoadingMatch(false);
          return;
        }

        // Hit the edge function so we get the full top-N response
        const matchResponse = await MatchingService.findMatches(seekerId, 2);

        if (cancelled) return;

        if (matchResponse.matches && matchResponse.matches.length > 0) {
          const guidesArr: GuideDetails[] = [];
          const scores: Record<string, number> = {};

          for (const m of matchResponse.matches) {
            const gd = (m as any).guide_details;
            if (gd) {
              guidesArr.push({
                guide_id: gd.guide_id,
                name: gd.name,
                location: gd.location,
                bio: gd.bio,
                experience_types: gd.experience_types,
                support_types: gd.support_types,
                focus_areas: gd.focus_areas,
                special_groups: gd.special_groups,
                religious_beliefs: gd.religious_beliefs,
              });
              scores[gd.guide_id] = m.total_score;
            }
          }

          if (guidesArr.length > 0) {
            setFetchedGuides(guidesArr);
            setFetchedMatchScores(scores);

            // Invalidate localStorage if the matched guide(s) changed
            try {
              const stored = localStorage.getItem('matching_result');
              if (stored) {
                const parsed = JSON.parse(stored);
                const cachedIds: string[] = parsed.guides?.map((g: any) => g.guide_id) ??
                  (parsed.guide ? [parsed.guide.guide_id] : []);
                const freshIds = guidesArr.map(g => g.guide_id);
                const guideChanged =
                  freshIds.length !== cachedIds.length ||
                  freshIds.some(id => !cachedIds.includes(id));
                if (guideChanged) {
                  localStorage.removeItem('matching_result');
                }
              }
            } catch {
              // ignore parse errors
            }

            return;
          }
        }

        // Safety net: fall back to the legacy single-row DB fetch if the
        // edge function returned nothing usable.
        const matchData = await fetchActiveMatch();
        if (cancelled) return;
        if (matchData) {
          setFetchedGuides([matchData.guide]);
          setFetchedMatchScores({ [matchData.guide.guide_id]: matchData.matchScore });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading active match:', error);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingMatch(false);
        }
      }
    };

    loadActiveMatch();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, propGuide, propGuides?.length, fetchedGuides.length]);

  // Check if seeker already has a session scheduled with any of the guides
  // If so, redirect to dashboard
  useEffect(() => {
    // Need to be authenticated and have guides to check
    if (!isAuthenticated || !guides.length) {
      return;
    }

    let cancelled = false;

    const checkAndRedirect = async () => {
      // Check if any guide has an existing session
      for (const g of guides) {
        const hasSession = await checkExistingSessionWithGuide(g.guide_id);

        if (cancelled) return;

        if (hasSession) {
          setHasExistingSession(true);
          navigate('/home', { replace: true });
          return;
        }
      }
      setHasExistingSession(false);
    };

    checkAndRedirect();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, guides.map(g => g.guide_id).join(','), navigate]);

  // Auto-open scheduler when navigating from email confirmation
  useEffect(() => {
    const state = routeLocation.state as { openScheduler?: boolean } | null;
    if (state?.openScheduler && isAuthenticated && guides.length > 0) {
      // Default to first guide when opening scheduler from email confirmation
      setSelectedGuide(guides[0]);
      setShowScheduler(true);
      // Clear the state so it doesn't re-trigger
      window.history.replaceState({}, '', window.location.href);
    }
  }, [routeLocation.state, isAuthenticated, guides.length]);

  // Prevent back navigation using history-forward trap (works reliably in Safari)
  // This is standard practice for post-authentication/onboarding flows
  useEffect(() => {
    // Push a new state to establish our position
    window.history.pushState(null, '', window.location.href);

    // When user tries to go back, immediately push them forward again
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handlers for profile view - now accept guide parameter
  const handleViewProfile = (guideToView: GuideDetails) => {
    setSelectedGuide(guideToView);
    setShowProfileView(true);
  };

  const handleBackFromProfile = () => {
    setShowProfileView(false);
    setSelectedGuide(null);
  };

  const handleScheduleFromProfile = (guideToSchedule: GuideDetails) => {
    setSelectedGuide(guideToSchedule);
    setShowProfileView(false);
    if (isAuthenticated) {
      setShowScheduler(true);
      setShowPasswordSetup(false);
    } else {
      setShowPasswordSetup(true);
    }
  };

  // Handlers for scheduling - now accept guide parameter
  const handleScheduleConsultation = (guideToSchedule: GuideDetails) => {
    setSelectedGuide(guideToSchedule);
    if (isAuthenticated) {
      setShowScheduler(true);
      setShowPasswordSetup(false);
    } else {
      setShowPasswordSetup(true);
    }
  };

  const handlePasswordSet = async (password: string) => {
    setSelectedPassword(password);
    setAccountSetupError('');

    // Move to scheduler after password is set
    setShowPasswordSetup(false);
    setShowScheduler(true);
  };

  const handleAccountCreated = (email: string) => {
    setConfirmationEmail(email);
    setShowPasswordSetup(false);
    setShowEmailConfirmation(true);
  };

  const handleEmailConfirmed = async () => {
    // Check if user is now authenticated
    const supabase = getSupabaseClient();
    const { data } = await supabase.auth.getSession();

    if (data.session?.user) {
      setIsAuthenticated(true);
      setShowEmailConfirmation(false);
      setShowScheduler(true);
    } else {
      // Still not confirmed, maybe show a message
      setShowEmailConfirmation(false);
      setShowPasswordSetup(true);
      setAccountSetupError('Please confirm your email before continuing.');
    }
  };

  const handleBackFromPasswordSetup = () => {
    setShowPasswordSetup(false);
  };

  const handleBackFromEmailConfirmation = () => {
    setShowEmailConfirmation(false);
    setShowPasswordSetup(true);
  };

  const handleBackFromScheduler = () => {
    setShowScheduler(false);
    if (!isAuthenticated) {
      setShowPasswordSetup(true); // Go back to password setup when not logged in
    }
  };

  const handleBookingComplete = (session: ScheduledSession) => {
    // Store the completed session and show success
    setCompletedSession(session);
    setShowScheduler(false);
    setShowBookingSuccess(true);
  };

  const handleBackFromBookingSuccess = () => {
    // Navigate to the home page after successful booking
    navigate('/home');
  };

  // Show booking success if completed
  if (showBookingSuccess && completedSession) {
    return (
      <BookingSuccess
        session={completedSession}
        onContinue={handleBackFromBookingSuccess}
      />
    );
  }

  // Show email confirmation if account was created
  if (showEmailConfirmation) {
    return (
      <EmailConfirmation
        email={confirmationEmail}
        onContinue={handleEmailConfirmed}
      />
    );
  }

  // Show password setup first
  if (showPasswordSetup) {
    return (
      <AccountPasswordSetup
        onBack={handleBackFromPasswordSetup}
        onPasswordSet={handlePasswordSet}
        onAccountCreated={handleAccountCreated}
        isLoading={false}
        error={accountSetupError}
      />
    );
  }

  // Show scheduler if requested - use selectedGuide
  if (showScheduler && selectedGuide) {
    return (
      <ConsultationScheduler
        guide={selectedGuide}
        password={selectedPassword}
        onBack={handleBackFromScheduler}
        onBookingComplete={handleBookingComplete}
        accountAlreadyCreated={isAuthenticated}
      />
    );
  }

  // Show profile view if requested - use selectedGuide
  if (showProfileView && selectedGuide) {
    return (
      <GuideProfileView
        guide={selectedGuide}
        matchScore={selectedGuide.guide_id ? matchScores[selectedGuide.guide_id] : undefined}
        onBack={handleBackFromProfile}
        onScheduleConsultation={handleScheduleFromProfile}
      />
    );
  }

  const renderStageHeader = () => (
    <>
      {/* Mobile header */}
      <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
        <div className="w-10" aria-hidden />
        <div className="flex flex-1 justify-center">
          <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
        </div>
        <div className="w-10" aria-hidden />
      </header>
      {/* Desktop header */}
      <div className="hidden lg:block">
        <SeekerHeader />
      </div>
    </>
  );

  const renderFooter = () => (
    <div className="hidden lg:block">
      <Footer />
    </div>
  );

  // Show loading state when fetching match from database or checking for existing session
  // hasExistingSession === null means we're still checking (only when authenticated with guides)
  const isCheckingExistingSession = isAuthenticated && guides.length > 0 && hasExistingSession === null;

  if (isLoadingMatch || isCheckingExistingSession) {
    return (
      <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
        <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
          {renderStageHeader()}
          <main className="flex flex-1 min-h-0 flex-col items-center justify-center text-center px-6 lg:px-[120px]">
            <div className="animate-fade-in flex flex-col items-center gap-4">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#304031]" />
              <h1 className="font-recoleta text-[32px] lg:text-[42px] font-normal leading-[1.1] text-[#304031]">
                Loading your match
              </h1>
              <p className="max-w-sm text-[16px] leading-relaxed text-[#214D3C]">
                We're retrieving your personalized guide match...
              </p>
            </div>
          </main>
        </div>
        {renderFooter()}
      </div>
    );
  }

  if (variant === 'match') {
    return (
      <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
        <div className="relative flex flex-1 w-full flex-col bg-onboarding-content-bg">
          {renderStageHeader()}

          {/* Content wrapper */}
          <div className="flex flex-1 flex-col lg:items-center lg:px-[72px] lg:py-10 lg:gap-10">
            <main className="flex flex-1 lg:flex-initial min-h-0 flex-col gap-6 overflow-y-auto lg:overflow-visible px-4 lg:px-0 pb-10 pt-6 lg:pt-0 w-full lg:w-[509px]">
              {/* Page title */}
              <h1 className="font-recoleta text-[32px] leading-[1.1] text-onboarding-card-green">
                Your guide match is ready
              </h1>
              <p className="font-helvetica text-lg leading-relaxed text-onboarding-text-black">
                Based on your preferences, needs and comfort levels, here are strongly aligned Guides for you:
              </p>

              {/* Guide cards - map over all guides */}
              {guides.map((g) => (
                <GuideCard
                  key={g.guide_id}
                  guide={g}
                  matchScore={matchScores[g.guide_id]}
                  seekerVisibleMessages={seekerVisibleMessagesByGuide[g.guide_id]}
                  enhancedProfile={enhancedProfiles[g.guide_id]}
                  onScheduleConsultation={handleScheduleConsultation}
                  onViewProfile={handleViewProfile}
                />
              ))}
            </main>
          </div>
        </div>

        {renderFooter()}
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
      <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
        {renderStageHeader()}

        {/* Content wrapper */}
        <div className="flex flex-1 flex-col lg:px-[120px] lg:py-16">
          <main className="flex flex-1 min-h-0 flex-col items-center justify-center text-center px-6 lg:px-0 pb-44 lg:pb-0">
            <div className="animate-fade-in flex flex-col items-center gap-4 lg:max-w-[480px]">
              <img
                src="/noguidesfound.webp"
                alt="No guides found"
                className="h-40 lg:h-48 w-auto"
              />
              <h1 className="font-recoleta text-[42px] lg:text-[64px] font-normal leading-[1.1] text-[#304031]">
                Still searching
              </h1>
              <p className="max-w-sm lg:max-w-none text-[18px] lg:text-[20px] leading-relaxed text-[#214D3C]">
                We haven't found the perfect guide yet, but our concierge team is expanding the search.
                Sit tight—we'll notify you as soon as we have someone aligned to your needs.
              </p>
            </div>
          </main>

          {/* Action button */}
          <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-[#304031]/18 bg-gradient-to-t from-[#FFFCEF] via-[#FFFCEF] to-[#FFFCEF]/0 px-6 pb-7 pt-5 shadow-[0_-12px_26px_-24px_rgba(48,64,49,0.55)] lg:static lg:inset-auto lg:z-auto lg:border-t-0 lg:bg-transparent lg:shadow-none lg:pt-8 lg:pb-0 lg:px-0 lg:max-w-[480px] lg:mx-auto">
            <a
              href="mailto:support@guidescollective.com"
              className="inline-flex w-full lg:w-auto items-center justify-center rounded-full border border-[#304031] bg-[#FFFCEF] px-6 lg:px-8 py-4 lg:py-3 text-lg lg:text-base font-medium lg:font-semibold text-[#304031] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.45)] lg:shadow-none transition duration-200 hover:-translate-y-0.5 lg:hover:translate-y-0 hover:bg-[#304031] hover:text-[#FFFCEF] lg:hover:bg-[#304031]/5 lg:hover:text-[#304031] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031] disabled:pointer-events-none disabled:translate-y-0 disabled:opacity-50"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>

      {renderFooter()}
    </div>
  );
};
